import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { toThousandSeparator } from '@eureka/utils/number-format';
import type { CompanyRepository } from '@/domains/company/services/company-repository';
import { CompanyApi } from '@/domains/company/apis/company-api';
import { E_ORGANIZATION_TYPE, ORG_ANS_TYPE, type CompanyInfo, type CompanyOriginalAddress } from '../../types';

interface CompanyDisplayAddress {
    displayCity: string
    displayCountry: string
    displayLocation: string
    countryEn: string
}

const COMPANY_EMPLOYEE_NUMBER_MAP = [10, 50, 100, 250, 500, 1000, 5000, 10000, 50000, 100000];

@injectable()
export class CompanyRepositoryImpl implements CompanyRepository {
    constructor(
        @inject(CompanyApi) private companyApi: CompanyApi
    ) {
    }

    _pickDisplayFromOriginalAddress(address?: CompanyOriginalAddress): CompanyDisplayAddress {
        if (!address) {
            return {
                displayLocation: '',
                displayCity: '',
                displayCountry: '',
                countryEn: '',
            }
        }
        const displayCountry = address.country || ''
        const displayCity = address.state || address.city || ''
        const displayLocation = displayCity && displayCountry ? `${displayCity}, ${displayCountry}` : displayCountry || displayCity
        return {
            displayCountry,
            displayCity,
            displayLocation,
            countryEn: address.country_en || '',
        }
    }

    _getCompanyOwnershipType(company: CompanyInfo, entityType: ORG_ANS_TYPE) {
        if (entityType === ORG_ANS_TYPE.ANS) {
            return company.ans_category?.toLowerCase()
        }

        if (company.normalized_entity_type_en === E_ORGANIZATION_TYPE.COMPANY || !company.normalized_entity_type_en) {
            return company?.offset_info?.ownership_type?.length ? company.offset_info.ownership_type[0] : ''
        } else {
            return company.normalized_entity_type_en
        }
    }

    _binarySearchNumber(target: number, arr = COMPANY_EMPLOYEE_NUMBER_MAP, start?: number, end?: number): string {
        start = start || 0
        end = end || arr.length - 1
        while (start <= end) {
            const mid = Math.floor((start + end) / 2)
            if (arr[mid] > target) {
                end = mid - 1
            } else {
                start = mid + 1
            }
        }
        const startLabel = arr[end]
        const endLabel = arr[start]

        if (!endLabel) {
            return `> ${toThousandSeparator(startLabel)}`
        }
        if (!startLabel) {
            return `< ${toThousandSeparator(endLabel)}`
        }

        return `${toThousandSeparator(startLabel)}-${toThousandSeparator(endLabel)}`
    }

    async getInfo(id: string, type: string) {
        const response = await this.companyApi.getInfo(id, type);

        return {
            ...response,
            ...{
                organizationType: this._getCompanyOwnershipType(response, ORG_ANS_TYPE.ONEID),
                foundedYear: response.founded_date ? String(response.founded_date).slice(0, 4) : '',
                location: this._pickDisplayFromOriginalAddress(response.original_address).displayLocation,
                employeeStr: response.employee_number ? this._binarySearchNumber(response.employee_number) : undefined,
            },
        }
    }
}
